import React from 'react';
import { X } from 'lucide-react';
import { AuditLogsFilter, AuditAction, AuditStatus, AuditResource } from '../../../services/audit.service';
import { ThemedSelect } from '@components/common/ThemedSelect';

interface AuditLogFiltersProps {
  filter: AuditLogsFilter;
  onFilterChange: (filter: AuditLogsFilter) => void;
}

export const AuditLogFilters: React.FC<AuditLogFiltersProps> = ({
  filter,
  onFilterChange,
}) => {
  const handleChange = (key: keyof AuditLogsFilter, value: any) => {
    onFilterChange({ ...filter, [key]: value, page: 1 });
  };

  const clearFilters = () => {
    onFilterChange({ page: 1, limit: 50 });
  };

  const hasActiveFilters = !!(
    filter.startDate ||
    filter.endDate ||
    filter.action ||
    filter.resource ||
    filter.status ||
    filter.userId
  );

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Date Range */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Start Date
          </label>
          <input
            type="datetime-local"
            value={filter.startDate || ''}
            onChange={(e) => handleChange('startDate', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white text-gray-900 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            End Date
          </label>
          <input
            type="datetime-local"
            value={filter.endDate || ''}
            onChange={(e) => handleChange('endDate', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white text-gray-900 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-500"
          />
        </div>

        {/* Action */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Action
          </label>
          <ThemedSelect
            value={filter.action || ''}
            onChange={(e) => handleChange('action', e.target.value || undefined)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white text-gray-900 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-500"
          >
            <option value="">All Actions</option>
            {Object.values(AuditAction).map((action) => (
              <option key={action} value={action}>
                {action.charAt(0).toUpperCase() + action.slice(1).toLowerCase().replace(/_/g, ' ')}
              </option>
            ))}
          </ThemedSelect>
        </div>

        {/* Resource */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Resource
          </label>
          <ThemedSelect
            value={filter.resource || ''}
            onChange={(e) => handleChange('resource', e.target.value || undefined)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white text-gray-900 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-500"
          >
            <option value="">All Resources</option>
            {Object.values(AuditResource).map((resource) => (
              <option key={resource} value={resource}>
                {resource.charAt(0).toUpperCase() + resource.slice(1)}
              </option>
            ))}
          </ThemedSelect>
        </div>

        {/* Status */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Status
          </label>
          <ThemedSelect
            value={filter.status || ''}
            onChange={(e) => handleChange('status', e.target.value || undefined)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white text-gray-900 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-500"
          >
            <option value="">All Statuses</option>
            {Object.values(AuditStatus).map((status) => (
              <option key={status} value={status}>
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </option>
            ))}
          </ThemedSelect>
        </div>

        {/* User Email */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            User Email
          </label>
          <input
            type="email"
            placeholder="Filter by email..."
            value={filter.userEmail || ''}
            onChange={(e) => handleChange('userEmail', e.target.value || undefined)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white text-gray-900 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-500"
          />
        </div>

        {/* Resource ID */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Resource ID
          </label>
          <input
            type="text"
            placeholder="Filter by resource ID..."
            value={filter.resourceId || ''}
            onChange={(e) => handleChange('resourceId', e.target.value || undefined)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white text-gray-900 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-500"
          />
        </div>
      </div>

      {/* Clear Filters Button */}
      {hasActiveFilters && (
        <div className="flex justify-end">
          <button
            onClick={clearFilters}
            className="flex items-center gap-1 px-3 py-1 text-sm text-red-600 hover:text-red-700 dark:text-red-400"
          >
            <X className="h-4 w-4" />
            Clear All Filters
          </button>
        </div>
      )}
    </div>
  );
};

export default AuditLogFilters;
